import { BUSINESS } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import Icon from "@/components/Icon";
import { useLang } from "@/i18n";

const NAV = [
    { label: "Home", href: "home" },
    { label: "About", href: "about" },
    { label: "Products", href: "products" },
    { label: "Services", href: "services" },
    { label: "Contact", href: "contact" },
];

const PRODUCT_LINKS = [
    "Inverter",
    "Battery",
    "Lithium Battery",
    "Solar",
    "CCTV",
    "Broadband",
];

export default function Footer() {
    const { t } = useLang();
    return (
        <footer data-testid="site-footer" className="bg-black border-t border-white/8 pt-16 pb-24 md:pb-10">
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
                <div>
                    <div className="flex items-center gap-2.5">
                        <img
                            src="/images/logo.png"
                            alt="Farakka Broadband logo"
                            className="w-9 h-9 rounded-full object-cover ring-1 ring-solar/50"
                        />
                        <span className="font-heading font-bold text-lg text-white">
                            Farakka <span className="text-cyan2">Broadband</span>
                        </span>
                    </div>
                    <p className="mt-4 text-sm text-white/55 leading-relaxed max-w-xs">
                        {t.footer.description}
                    </p>
                </div>

                <nav aria-label="Footer">
                    <h3 className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-white/45">{t.footer.links}</h3>
                    <ul className="mt-4 space-y-2.5">
                        {NAV.map((l) => (
                            <li key={l.href}>
                                <button
                                    data-testid={`footer-link-${l.href}`}
                                    onClick={() => scrollToId(l.href)}
                                    className="text-sm text-white/65 hover:text-cyan2 transition-colors"
                                >
                                    {t.nav[l.href]}
                                </button>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div>
                    <h3 className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-white/45">{t.footer.products}</h3>
                    <ul className="mt-4 space-y-2.5">
                        {t.footer.productLinks.map((p, i) => (
                            <li key={p}>
                                <button
                                    data-testid={`footer-product-${i}`}
                                    onClick={() => scrollToId("products")}
                                    className="text-sm text-white/65 hover:text-cyan2 transition-colors"
                                >
                                    {p}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h3 className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-white/45">{t.footer.contact}</h3>
                    <a
                        data-testid="footer-phone"
                        href={BUSINESS.tel}
                        className="mt-4 flex items-center gap-2.5 text-sm text-white font-semibold hover:text-cyan2 transition-colors"
                    >
                        <Icon name="phone" size={15} className="text-cyan2" />
                        {BUSINESS.phoneDisplay}
                    </a>
                    <div className="mt-2 space-y-1.5">
                        {BUSINESS.extraPhones.map((p, i) => (
                            <a
                                key={p}
                                data-testid={`footer-phone-alt-${i}`}
                                href={`tel:${p}`}
                                className="flex items-center gap-2.5 text-sm text-white/65 hover:text-cyan2 transition-colors"
                            >
                                <Icon name="phone" size={14} className="text-cyan2/60" />
                                {p}
                            </a>
                        ))}
                    </div>
                    <p className="mt-3 flex items-start gap-2.5 text-sm text-white/60 leading-relaxed">
                        <Icon name="mappin" size={15} className="text-solar shrink-0 mt-0.5" />
                        {BUSINESS.addressShort}
                    </p>
                    <p className="mt-2 flex items-start gap-2.5 text-sm text-white/60 leading-relaxed">
                        <Icon name="mappin" size={15} className="text-solar shrink-0 mt-0.5" />
                        {BUSINESS.address2Short}
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14 pt-6 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-white/40">
                    {t.footer.copyright}
                </p>
                <p className="font-mono2 text-[11px] tracking-[0.15em] text-white/40" data-testid="footer-gst">
                    GSTIN: {BUSINESS.gst}
                </p>
                <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-white/30">
                    Power · Solar · Security · Connectivity
                </p>
            </div>
        </footer>
    );
}
